import React from 'react';
import {
  Typography,
  Button,
  makeStyles,
  Box,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import LoginForm from './LoginForm';
import AddressContactModal from './AddressContactModal';
import EmailChangeModal from './EmailChangeModal';
import DeleteAccountModal from './DeleteAccountModal';
import { useAuth } from '../../../contexts/AuthContext';

const useStyles = makeStyles((theme) => ({
  loginCard: {
    maxWidth: 350,
    margin: '40px auto',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
    padding: theme.spacing(4, 3),
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      margin: theme.spacing(2),
      padding: theme.spacing(2, 1),
    },
  },
  accountHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  sectionContainer: {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(4),
  },
  sectionText: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(4),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  sectionValue: {
    fontWeight: 500,
  },
  warningBox: {
    background: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  loadingBox: {
    marginTop: '16px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addressInfoBox: {
    marginTop: '16px',
    marginBottom: '16px',
  },
  resaleInfoBox: {
    marginTop: '16px',
    marginBottom: '16px',
  },
  deleteAccountBox: {
    marginTop: '16px',
    marginBottom: '16px',
  },
}));

const AccountTab = () => {
  const classes = useStyles();
  const {
    isAuthenticated,
    logout,
    requestCode,
    verifyCode,
    loading,
    updateEmail,
    verifyEmailChangeCode,
    updateAddress,
    getAddressData,
    addressData,
    isProfileComplete,
    userEmail,
    customerResale,
    customerResaleLoading,
    deleteAccount,
  } = useAuth();
  const [showEmailModal, setShowEmailModal] = React.useState(false);
  const [showAddressModal, setShowAddressModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [addressLoading, setAddressLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  // Load address data when component mounts
  React.useEffect(() => {
    if (isAuthenticated) {
      setAddressLoading(true);
      getAddressData().finally(() => setAddressLoading(false));
    }
  }, [isAuthenticated]);

  const handleEmailUpdate = async (newEmailOrCode, isVerification = false) => {
    try {
      // For email change requests, we only pass the new email
      // For verification, we would use a different function (verifyEmailChange)
      const result = await updateEmail(newEmailOrCode);
      return result;
    } catch (error) {
      return { success: false, message: error.message || 'An error occurred' };
    }
  };

  const handleAddressUpdate = async (newAddressData) => {
    try {
      const result = await updateAddress(newAddressData);
      return result;
    } catch (error) {
      return { success: false, message: error.message || 'An error occurred' };
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const result = await deleteAccount();
      if (result.success) {
        // Account deleted successfully, user will be logged out automatically
        // The AuthContext will handle clearing the state
        return result;
      } else {
        throw new Error(result.message || 'Failed to delete account');
      }
    } catch (error) {
      throw error;
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isAuthenticated && !loading) {
    return (
      <div className={classes.loginCard}>
        <LoginForm onLogin={requestCode} onVerifyCode={verifyCode} />
      </div>
    );
  }

  if (!isAuthenticated && loading) {
    return (
      <Box
        style={{
          marginTop: '16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={40} />
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginLeft: '8px' }}
        >
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  const currentEmail = userEmail || addressData?.email;
  const profileComplete = isProfileComplete();

  return (
    <Box>
      {/* Profile Completion Warning */}
      {!profileComplete && !loading && (
        <Box className={classes.warningBox}>
          <Typography
            variant="body1"
            style={{ color: '#856404', fontWeight: 500, marginBottom: '8px' }}
          >
            ⚠️ Please Complete Your Address Information Below
          </Typography>
          <Typography variant="body2" style={{ color: '#856404' }}>
            Click the edit button below to complete your address information.
          </Typography>
        </Box>
      )}
      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={40} />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: '8px' }}
          >
            Loading profile information...
          </Typography>
        </Box>
      ) : (
        <>
          <Box className={classes.accountHeader}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={logout}
            >
              Logout
            </Button>
          </Box>
          <Divider />

          {/* Email Section */}

          {/* Address & Contact Section */}
          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionText}>
              <Typography variant="body1" className={classes.sectionLabel}>
                Customer and Address Information:
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setShowAddressModal(true)}
              disabled={addressLoading}
            >
              {addressData ? 'Edit' : 'Add'}
            </Button>
          </Box>

          {/* Display current address info */}
          {addressData?.address1 ? (
            <Box className={classes.addressInfoBox}>
              {addressData.businessName && (
                <Typography gutterBottom>{addressData.businessName}</Typography>
              )}
              <Typography gutterBottom>
                {addressData.firstName} {addressData.lastName}
              </Typography>
              <Typography gutterBottom>
                {addressData.address1}
                {addressData.address2 && `, ${addressData.address2}`}
              </Typography>
              <Typography gutterBottom>
                {addressData.city}, {addressData.state} {addressData.zipCode}
              </Typography>
              <Typography gutterBottom>Phone: {addressData.phone}</Typography>
              <Typography gutterBottom>
                {addressData.labAccountNumber &&
                  `Lab Account Number: ${addressData.labAccountNumber}`}
              </Typography>
            </Box>
          ) : (
            <Box className={classes.addressInfoBox}>
              <Typography color="error">
                No address information available. Please add your shipping /
                contact address.
              </Typography>
            </Box>
          )}

          <Divider style={{ marginTop: '20px' }} />

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionText}>
              <Typography variant="body1" className={classes.sectionLabel}>
                Email:
              </Typography>
              <Typography variant="body1" className={classes.sectionValue}>
                {currentEmail || 'Not set'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setShowEmailModal(true)}
            >
              {currentEmail ? 'Edit' : 'Add'}
            </Button>
          </Box>
          <Typography variant="body1" className={classes.sectionLabel}>
            Please note that editing your email will require you to verify your
            new email address.
          </Typography>

          <Divider style={{ marginTop: '20px', marginBottom: '20px' }} />

          {/* Customer Resale Status Section */}
          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionText}>
              <Typography variant="body1" className={classes.sectionLabel}>
                Resale Status:
              </Typography>
            </Box>
          </Box>

          {customerResaleLoading ? (
            <Box className={classes.resaleInfoBox}>
              <CircularProgress size={20} />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginLeft: '8px' }}
              >
                Checking resale status...
              </Typography>
            </Box>
          ) : customerResale === 'error' ? (
            <Box className={classes.resaleInfoBox}>
              <Typography>Error checking resale status</Typography>
              <Typography color="textSecondary">
                There was an error checking your resale status. Please try again
                later.
              </Typography>
            </Box>
          ) : customerResale ? (
            <Box className={classes.resaleInfoBox}>
              <Typography>✓ Resale Customer</Typography>
              <Typography color="textSecondary">
                Your account is registered as a resale account.
              </Typography>
              <Typography color="textSecondary">
                States Exempt:{' '}
                {Array.isArray(customerResale.taxExemptStates)
                  ? customerResale.taxExemptStates.join(', ')
                  : customerResale.taxExemptStates}
              </Typography>
              <Typography color="textSecondary" paragraph>
                Your orders that are delivered to the states above will be
                exempt from sales tax by default - if you order some personal
                photos that should be taxed there is a checkbox at checkout to
                say that order is not exempt.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.resaleInfoBox}>
              <Typography>Not a Resale Customer</Typography>
              <Typography color="textSecondary">
                Your account is not registered as a resale customer.
              </Typography>
              <Typography color="textSecondary">
                If your purchases are for resale and you have a resale license
                please contact the lab for more information.
              </Typography>
              <Typography color="textSecondary" paragraph>
                Please note the resale status shown here is based on the Lab
                Account number set in the Customer Information section above.
                The Lab Account number is required for resale orders.
              </Typography>
            </Box>
          )}

          <Divider style={{ marginTop: '20px' }} />

          {/* Delete Account Section */}
          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionText}>
              <Typography variant="body1" className={classes.sectionLabel}>
                Delete Account:
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </Button>
          </Box>
          <Box className={classes.deleteAccountBox}>
            <Typography variant="body2" color="textSecondary">
              This action will permanently delete your online account.
            </Typography>
          </Box>
          <Divider style={{ marginTop: '20px', marginBottom: '20px' }} />
        </>
      )}
      {/* Modals */}
      <EmailChangeModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        currentEmail={currentEmail}
        onSave={handleEmailUpdate}
        onVerifyCode={verifyEmailChangeCode}
      />
      <AddressContactModal
        open={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        currentData={addressData || {}}
        onSave={handleAddressUpdate}
      />
      <DeleteAccountModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default AccountTab;
